import { useEffect, useId, useRef, type MouseEvent, type ReactNode, type SyntheticEvent } from 'react';
import { Button } from './Button';
import s from './ui.module.css';

/** Keeps a native <dialog> in sync with `open`; Escape and backdrop clicks call onClose. */
function useModal(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);
  const props = {
    ref,
    onCancel: (e: SyntheticEvent) => {
      e.preventDefault();
      onClose();
    },
    onClick: (e: MouseEvent<HTMLDialogElement>) => {
      if (e.target === e.currentTarget) onClose(); // click on the backdrop
    },
  };
  return props;
}

export function Drawer({ open, title, onClose, children, actions }: { open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; actions?: ReactNode }) {
  const modal = useModal(open, onClose);
  const titleId = useId();
  return (
    <dialog className={s.drawer} aria-labelledby={titleId} {...modal}>
      {open && (
        <>
          <header className={s.drawerHead}>
            <h2 id={titleId}>{title}</h2>
            <div className="row">
              {actions}
              <Button variant="ghost" size="sm" onClick={onClose} aria-label="關閉">
                ✕
              </Button>
            </div>
          </header>
          <div className={s.drawerBody}>{children}</div>
        </>
      )}
    </dialog>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  busy?: boolean;
  /** Disable confirm until the form inside is valid. */
  canConfirm?: boolean;
  error?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

/** The explicit second step every lasting admin action goes through. */
export function ConfirmDialog({ open, title, children, confirmLabel, tone = 'primary', busy = false, canConfirm = true, error, onConfirm, onCancel }: ConfirmDialogProps) {
  const modal = useModal(open, () => !busy && onCancel());
  const titleId = useId();
  return (
    <dialog className={s.dialog} aria-labelledby={titleId} {...modal}>
      {open && (
        <form
          method="dialog"
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm && !busy) onConfirm();
          }}
        >
          <div className={s.dialogBody}>
            <h2 id={titleId}>{title}</h2>
            {children}
            {error}
          </div>
          <div className={s.dialogActions}>
            <Button onClick={onCancel} disabled={busy}>
              取消
            </Button>
            <Button type="submit" variant={tone === 'danger' ? 'dangerSolid' : 'primary'} busy={busy} disabled={!canConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </form>
      )}
    </dialog>
  );
}
